// adultFilterFromLists.js

// ─────────────────────────────────────────────
// 1) CONFIG – API + CACHE
// ─────────────────────────────────────────────

// Cloud Function URL from firebase deploy
const ADULT_HASH_API =
  "https://us-central1-dynqrforall.cloudfunctions.net/adultHashes";
// ↑ replace <your-project-id> and region if different

const CACHE_KEY = "dynqr_adult_hashes_cache_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h fallback if server expiresAt missing

const FALLBACK_KEYWORDS = [
  "porn",
  "porno",
  "pornography",
  "sex",
  "xxx",
  "adult",
  "nsfw",
  "camgirl",
  "escort",
  "xvideos",
  "redtube",
  "chatyourbate",
  "bongacams",
  "myfreecams",
  "livejasmin",
];

// In-memory
let hashedDomains = new Set();
let listsLoaded = false;
let listsLoadingPromise = null;

// ─────────────────────────────────────────────
// 2) CRYPTO HELPERS (AES-GCM + SHA-256)
// ─────────────────────────────────────────────

const CACHE_SECRET = "1234567890abcdef"; // 16 bytes

async function getCryptoKey() {
  const encoder = new TextEncoder();
  const keyMaterial = encoder.encode(CACHE_SECRET);
  if (keyMaterial.byteLength !== 16 && keyMaterial.byteLength !== 32) {
    throw new Error(
      `CACHE_SECRET must be 16 or 32 bytes, got ${keyMaterial.byteLength}`
    );
  }

  return crypto.subtle.importKey(
    "raw",
    keyMaterial,
    {name: "AES-GCM"},
    false,
    ["encrypt", "decrypt"],
  );
}

function uint8ToBase64(bytes) {
  let binary = "";
  const len = bytes.byteLength;
  const chunkSize = 0x8000;
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

function base64ToUint8(b64) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function encryptToBase64(plaintext) {
  const key = await getCryptoKey();
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  const iv = crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await crypto.subtle.encrypt(
    {name: "AES-GCM", iv},
    key,
    data,
  );

  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.byteLength);

  return uint8ToBase64(combined);
}

async function decryptFromBase64(b64) {
  const key = await getCryptoKey();
  const combined = base64ToUint8(b64);

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    {name: "AES-GCM", iv},
    key,
    ciphertext,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

// SHA-256 → first 8 bytes as hex string
async function hashDomain64(domain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(domain.toLowerCase());
  const hashBuf = await crypto.subtle.digest("SHA-256", data);
  const hashBytes = new Uint8Array(hashBuf).slice(0, 8);
  return Array.from(hashBytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─────────────────────────────────────────────
// 3) LOAD HASHES FROM FUNCTION (+ ENCRYPTED CACHE)
// ─────────────────────────────────────────────

async function loadAdultDomainLists() {
  if (listsLoaded) return;
  if (listsLoadingPromise) return listsLoadingPromise;

  listsLoadingPromise = (async () => {
    try {
      // 3.1) Try encrypted cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const decrypted = await decryptFromBase64(cached);
          const parsed = JSON.parse(decrypted);
          if (
            parsed &&
            parsed.expiresAt > Date.now() &&
            Array.isArray(parsed.hashes)
          ) {
            hashedDomains = new Set(parsed.hashes);
            listsLoaded = true;
            console.log(
              "Adult hashed list loaded from cache, count =",
              hashedDomains.size,
            );
            return;
          }
        } catch (e) {
          console.warn(
            "Failed to decrypt or parse adult hash cache, will reload lists",
            e,
          );
        }
      }

      // 3.2) Fetch from your Cloud Function (CSP-safe)
      const res = await fetch(ADULT_HASH_API, {method: "GET"});
      if (!res.ok) {
        console.error("Failed to fetch adult hashes API", res.status);
        hashedDomains = new Set();
        listsLoaded = true;
        return;
      }

      const data = await res.json();
      const hashes = Array.isArray(data.hashes) ? data.hashes : [];
      hashedDomains = new Set(hashes);
      listsLoaded = true;

      const expiresAt =
        typeof data.expiresAt === "number"
          ? data.expiresAt
          : Date.now() + CACHE_TTL_MS;

      console.log(
        "Adult hashed lists loaded from API, count =",
        hashedDomains.size,
      );

      // 3.3) Encrypt and cache
      try {
        const payload = JSON.stringify({
          hashes,
          expiresAt,
        });
        const encrypted = await encryptToBase64(payload);
        localStorage.setItem(CACHE_KEY, encrypted);
      } catch (e) {
        console.warn("Could not cache adult hash list", e);
      }
    } catch (err) {
      console.error("Unexpected error loading adult hashes", err);
      hashedDomains = new Set();
      listsLoaded = true;
    }
  })();

  return listsLoadingPromise;
}

// ─────────────────────────────────────────────
// 4) PUBLIC API – checkAdultUrl
// ─────────────────────────────────────────────

export async function checkAdultUrl(urlString) {
  if (!urlString) return {isAdult: false, reason: null};

  let url;
  try {
    url = new URL(urlString);
  } catch {
    return {isAdult: false, reason: null};
  }

  const host = url.hostname.toLowerCase();
  const full = url.toString().toLowerCase();

  await loadAdultDomainLists();

  // 4.1) Hash host & parents and compare
  if (hashedDomains.size > 0) {
    const parts = host.split(".");
    for (let i = 0; i < parts.length - 1; i++) {
      const candidate = parts.slice(i).join(".");
      const h = await hashDomain64(candidate);
      if (hashedDomains.has(h)) {
        return {
          isAdult: true,
          reason: `Domain "${candidate}" is blocked as it contains adult or explicit content and can not be used.`,
        };
      }
    }
  }

  // 4.2) Fallback keyword scan
  if (FALLBACK_KEYWORDS.some((kw) => full.includes(kw))) {
    return {
      isAdult: true,
      reason:
        "This URL appears to contain adult or explicit content and cannot be used.",
    };
  }

  return {isAdult: false, reason: null};
}
